import React, { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { formatDate, formatDateTime, timeAgo } from '@/utils/dateFormat'
import {
  getApplication,
  getApplicationHistory,
  transitionApplication,
  flagApplication,
  assignApplication,
  updateApplication,
  getTapAssignees,
  TapAssignee,
  getTypeformSubmission,
  TypeformSubmission,
  getPriorApplications,
  PriorApplicationsResult,
  // Bug #240: share-application uses the existing access-grants endpoints.
  getAccessGrants,
  createAccessGrant,
  revokeAccessGrant,
} from '@/api/tapApplications'
import { createNote } from '@/api/tapResources'
import { getUsers } from '@/api/users'
import { PageHeader } from '@/components/ui/PageHeader'
import { Modal } from '@/components/ui/Modal'
import {
  TapApplication,
  TapWorkflowEvent,
  TapStage,
  TapStatusFlag,
  TapDecisionOutcome,
  TapAccessGrant,
  User,
} from '@/types/index'
import { ArrowLeft, AlertCircle, ChevronRight, FileText, Info, Share2, X } from 'lucide-react'
import StageBadge from './components/StageBadge'
import StatusFlagBadge from './components/StatusFlagBadge'
import SlaIndicator from './components/SlaIndicator'
import WorkflowTimeline from './components/WorkflowTimeline'
import SummaryEditor from './components/SummaryEditor'
import ApplicantEditor from './components/ApplicantEditor'
import ResumeWidget from './components/ResumeWidget'
import ApplicationDetailsEditor from './components/ApplicationDetailsEditor'
import SkillsPanel from './components/SkillsPanel'
import CertificationsPanel from './components/CertificationsPanel'
import PhoneScreenTab from './components/PhoneScreenTab'
import InterviewsTab from './components/InterviewsTab'
import AssessmentTab from './components/AssessmentTab'
import ReferenceChecksTab from './components/ReferenceChecksTab'
import OfferTab from './components/OfferTab'
import NotesTab from './components/NotesTab'
import DocumentsTab from './components/DocumentsTab'
import EmailsTab from './components/EmailsTab'

// Feature #199: Decision renamed to Outcome.
const ALL_STAGES: TapStage[] = ['Review', 'PhoneScreen', 'Interview', 'Assessment', 'ReferenceCheck', 'Offer', 'Outcome']
// Feature #223: outcome values are exposed as Status Flags too. Order:
// operational flags first, then outcome flags so the existing dropdown order
// is preserved and new entries appear at the bottom.
const ALL_FLAGS: (TapStatusFlag | '')[] = ['', 'Urgent', 'OnHold', 'Withdrawn', 'Archived', 'Successful', 'Unsuccessful', 'TalentPool']
const FLAG_LABELS: Record<string, string> = {
  OnHold: 'On Hold',
  TalentPool: 'Talent Pool',
}

const stageLabels: Record<string, string> = {
  Review: 'Review',
  PhoneScreen: 'Phone Screen',
  Interview: 'Interview',
  Assessment: 'Assessment',
  ReferenceCheck: 'Reference Check',
  Offer: 'Offer',
  // Feature #199: Decision renamed to Outcome.
  Outcome: 'Outcome',
}

type TabKey = 'summary' | 'phoneScreen' | 'interviews' | 'assessment' | 'referenceChecks' | 'offer' | 'documents' | 'emails' | 'notes' | 'history'

const tabs: { key: TabKey; label: string }[] = [
  { key: 'summary', label: 'Summary' },
  { key: 'phoneScreen', label: 'Phone Screen' },
  { key: 'interviews', label: 'Interviews' },
  { key: 'assessment', label: 'Assessment' },
  { key: 'referenceChecks', label: 'Reference Checks' },
  { key: 'offer', label: 'Offer' },
  { key: 'documents', label: 'Documents' },
  // Feature #162: Emails tab — compose/send outbound + view inbound messages.
  { key: 'emails', label: 'Emails' },
  { key: 'notes', label: 'Notes' },
  { key: 'history', label: 'History' },
]

const ApplicationDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const [application, setApplication] = useState<TapApplication | null>(null)
  const [history, setHistory] = useState<TapWorkflowEvent[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const [activeTab, setActiveTab] = useState<TabKey>('summary')

  // Modal states
  const [transitionModalOpen, setTransitionModalOpen] = useState(false)
  const [flagModalOpen, setFlagModalOpen] = useState(false)
  const [assignModalOpen, setAssignModalOpen] = useState(false)
  // Feature #192: signals to auto-open modals after a transition.
  const [autoOpenPhoneScreen, setAutoOpenPhoneScreen] = useState(false)
  const [autoOpenInterview, setAutoOpenInterview] = useState(false)
  // Feature #238: open the Compose Email modal automatically when the
  // agent transitions to Outcome=Unsuccessful — saves a click on the
  // most common follow-up step.
  const [autoOpenComposeEmail, setAutoOpenComposeEmail] = useState(false)

  // Bug #240: Share-application UI state. Backend infrastructure
  // (TapApplicationAccessGrant + create/list/revoke endpoints +
  // require_tap_application_access dependency) was already in place
  // — this work item is just the missing UI control.
  const [shareModalOpen, setShareModalOpen] = useState(false)
  const [accessGrants, setAccessGrants] = useState<TapAccessGrant[]>([])
  const [accessGrantsLoading, setAccessGrantsLoading] = useState(false)
  const [shareableUsers, setShareableUsers] = useState<User[]>([])
  const [shareUserSearch, setShareUserSearch] = useState('')
  const [shareUserId, setShareUserId] = useState('')
  const [shareGrantType, setShareGrantType] = useState<'Viewer' | 'Approver'>('Viewer')
  const [shareReason, setShareReason] = useState('')
  const [shareSaving, setShareSaving] = useState(false)
  const [shareUserDropdownOpen, setShareUserDropdownOpen] = useState(false)
  const shareUserDropdownRef = useRef<HTMLDivElement>(null)
  // Feature #201: Typeform submission viewer modal.
  const [submissionModalOpen, setSubmissionModalOpen] = useState(false)
  const [submission, setSubmission] = useState<TypeformSubmission | null>(null)
  const [submissionLoading, setSubmissionLoading] = useState(false)
  // Feature #223: Decision capture moved inline onto the Transition modal.
  // decisionOutcome / decisionNote are still used (set by the radios and
  // textarea inside the Transition modal); decisionModalOpen retired.
  const [decisionOutcome, setDecisionOutcome] = useState<TapDecisionOutcome | ''>('')
  const [decisionNote, setDecisionNote] = useState('')
  const [transitionStage, setTransitionStage] = useState('')
  const [transitionNote, setTransitionNote] = useState('')
  const [selectedFlag, setSelectedFlag] = useState<string>('')
  // Bug #147: `assignTo` holds the selected assignee's user UUID (or '' for
  // unassign). Previously this was a freeform email/name string that the
  // backend rejected with a 500.
  const [assignTo, setAssignTo] = useState('')
  const [assignees, setAssignees] = useState<TapAssignee[]>([])
  const [assigneesLoading, setAssigneesLoading] = useState(false)
  const [assigneesError, setAssigneesError] = useState<string | null>(null)
  // Feature #224: prior applications by the same applicant.
  const [priorApps, setPriorApps] = useState<PriorApplicationsResult | null>(null)
  // Bug #151: persist "we already fetched" across effect runs so an empty
  // assignee list (no-one in the manage_tap role yet) doesn't re-trigger the
  // fetch. Using a ref avoids adding another dep that would re-fire the effect.
  const assigneesFetchedRef = useRef(false)

  const fetchData = async () => {
    if (!id) return
    setIsLoading(true)
    setError(null)
    try {
      const [app, hist] = await Promise.all([
        getApplication(id),
        getApplicationHistory(id),
      ])
      setApplication(app)
      setHistory(hist)
      // Feature #224: fetch prior applications in the background (non-blocking).
      getPriorApplications(id).then(setPriorApps).catch(() => setPriorApps(null))
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to load application'))
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [id])

  // Bug #240: outside-click handler for the share-modal user picker.
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (shareUserDropdownRef.current && !shareUserDropdownRef.current.contains(e.target as Node)) {
        setShareUserDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  // Feature #192: map stages to their corresponding tab keys so the UI can
  // auto-navigate after a transition.
  const stageToTab: Record<string, TabKey> = {
    Review: 'summary',
    PhoneScreen: 'phoneScreen',
    Interview: 'interviews',
    Assessment: 'assessment',
    ReferenceCheck: 'referenceChecks',
    Offer: 'offer',
    Outcome: 'summary',
  }

  const handleTransition = async () => {
    if (!id || !transitionStage) return
    // Feature #223: when transitioning to Outcome, the user must pick an
    // outcome (Successful / Unsuccessful / Talent Pool). The legacy Record
    // Decision modal has been retired — the decision is now captured inline.
    if (transitionStage === 'Outcome' && !decisionOutcome) return
    try {
      // Persist the outcome on the application *before* the stage transition,
      // so any backend hooks that fire on stage change (notifications, etc.)
      // see the final outcome value.
      let updated = application
      if (transitionStage === 'Outcome' && decisionOutcome) {
        updated = await updateApplication(id, {
          decisionOutcome,
          summary: decisionNote
            ? [(application?.summary || '').trim(), `Decision note: ${decisionNote}`]
                .filter(Boolean)
                .join('\n\n')
            : application?.summary,
        })
        // Bug #239: the canonical TapStatusFlag values for the three
        // outcomes are exactly the same strings as the decisionOutcome
        // values (`Successful`, `Unsuccessful`, `TalentPool` — see #223).
        // Auto-stamp the matching status flag so the recruiter doesn't
        // have to open the Flag modal afterwards. flagApplication is the
        // existing endpoint the Flag modal uses, so behaviour stays
        // consistent (workflow event emitted, etc.).
        if (decisionOutcome === 'Successful' || decisionOutcome === 'Unsuccessful' || decisionOutcome === 'TalentPool') {
          try {
            updated = await flagApplication(id, decisionOutcome)
          } catch { /* non-fatal — outcome was already saved */ }
        }
        // Feature #197: also drop a structured note for the audit trail.
        if (decisionNote) {
          try {
            await createNote(id, `[Decision Note] ${decisionOutcome}: ${decisionNote}`, application?.stage || 'Offer')
          } catch { /* supplementary */ }
        }
      }
      const note = transitionNote
        || (transitionStage === 'Outcome' && decisionOutcome ? `Decision recorded: ${decisionOutcome}` : undefined)
      updated = await transitionApplication(id, transitionStage, note)
      setApplication(updated)
      setTransitionModalOpen(false)
      // Feature #192: auto-navigate to the relevant tab after transition.
      const targetTab = stageToTab[transitionStage]
      // Feature #238: when the outcome is Unsuccessful, override the
      // default Outcome→Summary tab navigation and jump to the Emails
      // tab instead, then signal it to auto-open the Compose modal —
      // the agent's natural next step is sending the regret note.
      if (transitionStage === 'Outcome' && decisionOutcome === 'Unsuccessful') {
        setActiveTab('emails')
        setAutoOpenComposeEmail(true)
      } else if (transitionStage === 'PhoneScreen') {
        // Feature #249: phone-screen transition now opens the Compose
        // Email modal on the Emails tab instead of the booking modal.
        // The previous behaviour (auto-open the Phone Screen booking
        // modal — Feature #192) is gone; the user wants to send the
        // candidate the booking link via email first, then capture the
        // booking when it comes back. The PhoneScreen tab still has its
        // own "+ Book screen" action for the explicit case.
        setActiveTab('emails')
        setAutoOpenComposeEmail(true)
      } else if (targetTab) {
        setActiveTab(targetTab)
      }
      // Feature #192: auto-open modal for Interview stage.
      // (PhoneScreen handled above — Feature #249.)
      if (transitionStage === 'Interview') setAutoOpenInterview(true)
      setTransitionStage('')
      setTransitionNote('')
      setDecisionOutcome('')
      setDecisionNote('')
      fetchData()
    } catch {
      alert('Failed to transition application')
    }
  }

  // Bug #240: open the Share modal — fetches existing grants + users
  // lazily on open so we don't pay the network cost for users who never
  // share an application.
  const openShareModal = async () => {
    setShareModalOpen(true)
    setShareUserSearch('')
    setShareUserId('')
    setShareGrantType('Viewer')
    setShareReason('')
    setShareUserDropdownOpen(false)
    if (!id) return
    setAccessGrantsLoading(true)
    try {
      const [grants, usersPage] = await Promise.all([
        getAccessGrants(id),
        // Pull a generous first page; for orgs > 100 users we'd switch
        // to a search-as-you-type backend lookup, but the picker
        // dropdown also caps display at 20 entries.
        getUsers({ pageSize: 100 }).catch(() => ({ items: [] as User[], total: 0, page: 1, pageSize: 0, pages: 0 })),
      ])
      setAccessGrants(grants.filter((g) => g.isActive && !g.revokedAt))
      setShareableUsers(usersPage.items || [])
    } catch {
      setAccessGrants([])
    } finally {
      setAccessGrantsLoading(false)
    }
  }

  const handleAddShare = async () => {
    if (!id || !shareUserId) return
    setShareSaving(true)
    try {
      await createAccessGrant(id, {
        user_id: shareUserId,
        grant_type: shareGrantType,
        reason: shareReason || undefined,
      })
      // Refresh the grants list inline so the new row appears without
      // closing the modal — the user can keep adding shares.
      const grants = await getAccessGrants(id)
      setAccessGrants(grants.filter((g) => g.isActive && !g.revokedAt))
      setShareUserId('')
      setShareUserSearch('')
      setShareReason('')
    } catch (err) {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      alert(typeof detail === 'string' ? detail : 'Failed to share application')
    } finally {
      setShareSaving(false)
    }
  }

  const handleRevokeShare = async (grant: TapAccessGrant) => {
    if (!id) return
    if (!confirm('Revoke access for this user?')) return
    try {
      await revokeAccessGrant(id, grant.id)
      setAccessGrants((prev) => prev.filter((g) => g.id !== grant.id))
    } catch {
      alert('Failed to revoke access')
    }
  }

  const handleFlag = async () => {
    if (!id) return
    try {
      const updated = await flagApplication(id, selectedFlag || null)
      setApplication(updated)
      setFlagModalOpen(false)
      setSelectedFlag('')
    } catch {
      alert('Failed to update flag')
    }
  }

  // Feature #223: handleDecision was removed. The decision capture is now
  // inline on the Transition modal — see handleTransition's Outcome branch.

  // Bug #147: support both "assign to UUID" and "unassign" (empty value → null).
  // Surface the backend's 400 detail so the user sees *why* assignment failed
  // (invalid UUID, inactive user, missing role) rather than a generic alert.
  const handleAssign = async () => {
    if (!id) return
    try {
      const updated = await assignApplication(id, assignTo ? assignTo : null)
      setApplication(updated)
      setAssignModalOpen(false)
      setAssignTo('')
    } catch (err) {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      alert(detail || 'Failed to assign application')
    }
  }

  // Load the list of eligible assignees once when the modal first opens.
  // Keeping the fetch lazy avoids pinging /tap/assignees for every page view.
  //
  // Bug #151: the previous version depended on `assignees.length` which meant
  // that when the backend returned an empty list (no-one with the manage_tap
  // role yet), the effect would re-fire forever — the guard `assignees.length
  // > 0` stayed false after each fetch, and `assigneesLoading` flipping back
  // to false re-triggered the effect. The fix uses a ref that records "we've
  // attempted the fetch" so an empty but successful response is respected.
  //
  // Bug #163: #151's fix still allowed an infinite retry loop on *error* — the
  // ref was only set inside `.then` (the success path), so a 403/500/network
  // failure left the ref `false`, `assigneesLoading` flipped true→false, the
  // effect re-ran, and we hammered the backend. Fix is to mark the ref
  // "attempted" in `.finally` regardless of outcome, drop `assigneesLoading`
  // from the dep array (it was only re-triggering), and reset the ref when
  // the modal closes so the user can retry by reopening.
  useEffect(() => {
    if (!assignModalOpen) {
      // Modal closed — reset the "attempted" ref so the next open retries.
      // Also clears any stale error from a previous attempt.
      assigneesFetchedRef.current = false
      setAssigneesError(null)
      return
    }
    if (assigneesFetchedRef.current) return
    let cancelled = false
    setAssigneesLoading(true)
    setAssigneesError(null)
    getTapAssignees()
      .then((list) => {
        if (!cancelled) setAssignees(list)
      })
      .catch((err) => {
        if (!cancelled) {
          // Surface a more useful error than a silent spinner when the API
          // returns 403 (user doesn't hold MANAGE_TAP) — previously the modal
          // just sat on "Loading eligible users…" because the catch block had
          // no effect past toggling the error flag.
          const detail =
            (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
          setAssigneesError(detail || 'Unable to load assignees')
        }
      })
      .finally(() => {
        if (!cancelled) {
          // Mark the attempt as complete regardless of outcome so we don't
          // retry-loop on repeated failure.
          assigneesFetchedRef.current = true
          setAssigneesLoading(false)
        }
      })
    return () => {
      cancelled = true
    }
  }, [assignModalOpen])

  // Feature #201: load and display the Typeform submission.
  const handleViewSubmission = async () => {
    if (!id) return
    setSubmissionModalOpen(true)
    if (submission) return // already loaded
    setSubmissionLoading(true)
    try {
      const data = await getTypeformSubmission(id)
      setSubmission(data)
    } catch {
      // Will show an empty state in the modal
    } finally {
      setSubmissionLoading(false)
    }
  }

  const buttonStyle: React.CSSProperties = {
    padding: '8px 16px',
    backgroundColor: 'var(--color-magenta)',
    color: 'var(--color-white)',
    border: 'none',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: 500,
    cursor: 'pointer',
    fontFamily: 'var(--font-family)',
  }

  const secondaryButtonStyle: React.CSSProperties = {
    ...buttonStyle,
    backgroundColor: 'var(--color-white)',
    color: 'var(--color-black)',
    border: '1px solid var(--color-light-grey)',
  }

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '8px 12px',
    border: '1px solid var(--color-light-grey)',
    borderRadius: '6px',
    fontSize: '14px',
    fontFamily: 'var(--font-family)',
  }

  const labelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: '14px',
    fontWeight: 500,
    marginBottom: '4px',
  }

  const cardStyle: React.CSSProperties = {
    backgroundColor: 'var(--color-white)',
    border: '1px solid var(--color-light-grey)',
    borderRadius: '8px',
    padding: '20px',
    marginBottom: '16px',
  }

  const fieldRowStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '16px',
    marginBottom: '12px',
  }

  const fieldLabelStyle: React.CSSProperties = {
    fontSize: '12px',
    fontWeight: 600,
    color: 'var(--color-dark-grey)',
    textTransform: 'uppercase',
    marginBottom: '4px',
  }

  const fieldValueStyle: React.CSSProperties = {
    fontSize: '14px',
    color: 'var(--color-black)',
  }

  if (isLoading) {
    return (
      <div style={{ padding: '0' }}>
        <PageHeader title="Loading..." />
        <div style={{ padding: '40px', textAlign: 'center', color: 'var(--color-dark-grey)' }}>
          Loading application...
        </div>
      </div>
    )
  }

  if (error || !application) {
    return (
      <div style={{ padding: '0' }}>
        <PageHeader title="Error" />
        <div style={{ padding: '24px' }}>
          <div style={{
            backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '6px',
            padding: '12px 16px', display: 'flex', gap: '10px', alignItems: 'flex-start',
          }}>
            <AlertCircle size={20} style={{ color: '#dc2626', flexShrink: 0, marginTop: '2px' }} />
            <div>
              <p style={{ margin: 0, color: '#dc2626', fontWeight: 500 }}>Error loading application</p>
              <p style={{ margin: '4px 0 0 0', color: '#991b1b', fontSize: '13px' }}>
                {error?.message ?? 'Application not found'}
              </p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const renderSummaryTab = () => (
    <div>
      {/* Feature #190: Applicant + Resume widget side by side */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '16px', alignItems: 'start' }}>
        {/* Bug #167 — person-first: applicant block (name/email) is inline-editable. */}
        <ApplicantEditor application={application!} onUpdated={setApplication} />
        <ResumeWidget applicationId={application!.id} />
      </div>
      {/* Feature #153 — editable summary + preferred employment type. */}
      <SummaryEditor application={application!} onUpdated={setApplication} />
      {/* Feature #153 — application skills with proficiency. */}
      <SkillsPanel applicationId={application!.id} />
      {/* Feature #218 — application certifications. */}
      <CertificationsPanel applicationId={application!.id} />
      {/* Feature #261: Application Details section moved below
          Certifications. The recruiter wants the review-time content
          (Review Notes, Skills, Certifications) front-loaded; the
          application metadata (vacancy code, stage, flag, decision,
          SLA, etc.) is reference material that drops to the bottom of
          the tab. */}
      <ApplicationDetailsEditor
        application={application!}
        onUpdated={setApplication}
        formatDate={formatDate}
      />

      <div style={cardStyle}>
        <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: 600, color: 'var(--color-black)' }}>
          Timestamps
        </h3>
        <div style={fieldRowStyle}>
          <div>
            <div style={fieldLabelStyle}>Created</div>
            <div style={fieldValueStyle}>{formatDateTime(application.createdAt)}</div>
          </div>
          <div>
            <div style={fieldLabelStyle}>Last Updated</div>
            <div style={fieldValueStyle}>{formatDateTime(application.updatedAt)}</div>
          </div>
        </div>
      </div>
    </div>
  )

  // Features #154–#161 — each tab is implemented in its own component file.
  // Feature #192: pass auto-open signals to child tabs.
  const renderPhoneScreenTab = () => (
    <PhoneScreenTab
      applicationId={application.id}
      autoOpenBook={autoOpenPhoneScreen}
      onAutoOpenHandled={() => setAutoOpenPhoneScreen(false)}
    />
  )
  const renderInterviewsTab = () => (
    <InterviewsTab
      applicationId={application.id}
      autoOpenSchedule={autoOpenInterview}
      onAutoOpenHandled={() => setAutoOpenInterview(false)}
    />
  )
  const renderAssessmentTab = () => (
    // Feature #248: Position Title is mirrored at the top of the
    // Assessment form, so the tab needs the parent application + a
    // refresh callback to push edits back through fetchData().
    <AssessmentTab
      applicationId={application.id}
      application={application}
      onApplicationUpdated={fetchData}
    />
  )
  const renderReferenceChecksTab = () => <ReferenceChecksTab applicationId={application.id} />
  const renderOfferTab = () => <OfferTab applicationId={application.id} />
  const renderDocumentsTab = () => <DocumentsTab applicationId={application.id} />
  const renderNotesTab = () => <NotesTab applicationId={application.id} currentStage={application.stage} />
  // Feature #162 — Emails tab. Uses the applicant email as the default
  // recipient when available so recruiters don't have to retype it.
  const renderEmailsTab = () => (
    <EmailsTab
      applicationId={application.id}
      defaultToAddress={application.applicant?.email}
      autoOpenCompose={autoOpenComposeEmail}
      onAutoOpenHandled={() => setAutoOpenComposeEmail(false)}
    />
  )

  const renderHistoryTab = () => (
    <div>
      <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: 600, color: 'var(--color-black)' }}>
        Workflow History
      </h3>
      <WorkflowTimeline events={history} />
    </div>
  )

  const renderTabContent = () => {
    switch (activeTab) {
      case 'summary': return renderSummaryTab()
      case 'phoneScreen': return renderPhoneScreenTab()
      case 'interviews': return renderInterviewsTab()
      case 'assessment': return renderAssessmentTab()
      case 'referenceChecks': return renderReferenceChecksTab()
      case 'offer': return renderOfferTab()
      case 'documents': return renderDocumentsTab()
      case 'emails': return renderEmailsTab()
      case 'notes': return renderNotesTab()
      case 'history': return renderHistoryTab()
      default: return null
    }
  }

  return (
    <div style={{ padding: '0' }}>
      <PageHeader
        // Feature #169: h1 is the applicant's full name (falls back to
        // position title → reference number for legacy records). Subtitle
        // shows the received date instead of the application type.
        title={(() => {
          const applicantName = [application.applicant?.firstName, application.applicant?.lastName]
            .filter(Boolean)
            .join(' ')
          if (applicantName) return applicantName
          if (application.positionTitle) return application.positionTitle
          return application.referenceNumber
        })()}
        // Feature #187: show relative time ("X days ago") with hover tooltip
        // showing the actual date.
        subtitle={
          application.receivedAt
            ? timeAgo(application.receivedAt)
            : undefined
        }
        subtitleTitle={
          application.receivedAt
            ? `Received: ${formatDate(application.receivedAt)}`
            : undefined
        }
        actions={
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
            <button
              onClick={() => navigate('/tap/applications')}
              style={secondaryButtonStyle}
            >
              <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <ArrowLeft size={14} /> Back
              </span>
            </button>
            <button
              onClick={() => {
                setTransitionStage('')
                setTransitionNote('')
                // Feature #223: prefill any prior decisionOutcome so re-transitioning
                // to Outcome surfaces the existing choice instead of starting blank.
                setDecisionOutcome(application.decisionOutcome || '')
                setDecisionNote('')
                setTransitionModalOpen(true)
              }}
              style={buttonStyle}
            >
              <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                Transition <ChevronRight size={14} />
              </span>
            </button>
            <button
              onClick={() => {
                setSelectedFlag(application.statusFlag || '')
                setFlagModalOpen(true)
              }}
              style={secondaryButtonStyle}
            >
              Flag
            </button>
            <button
              onClick={() => {
                setAssignTo(application.assignedTo || '')
                setAssignModalOpen(true)
              }}
              style={secondaryButtonStyle}
            >
              Assign
            </button>
            {/* Bug #240: Share — grant per-application Viewer / Approver
                access to a user who doesn't hold the MANAGE_TAP role.
                Backend grant infrastructure already existed under
                require_tap_application_access; this just wires the UI. */}
            <button onClick={openShareModal} style={secondaryButtonStyle}>
              <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <Share2 size={14} /> Share
              </span>
            </button>
            {/* Feature #201: View Submission button — only for Typeform-sourced apps. */}
            {application.sourceSystem === 'typeform' && (
              <button onClick={handleViewSubmission} style={secondaryButtonStyle}>
                <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <FileText size={14} /> View Submission
                </span>
              </button>
            )}
            {/* Feature #223: Record Decision button removed. The decision
                outcome is now captured inline on the Transition modal when
                the user moves the application to Outcome. The decision is
                also expressible as a Status Flag (Successful / Unsuccessful
                / Talent Pool) via the Flag modal. */}
          </div>
        }
      />

      {/* Stage + flag summary bar */}
      <div style={{
        padding: '12px 24px',
        backgroundColor: 'var(--color-white)',
        borderBottom: '1px solid var(--color-light-grey)',
        display: 'flex',
        gap: '16px',
        alignItems: 'center',
      }}>
        <SlaIndicator status={application.slaStatus} />
        <StageBadge stage={application.stage} />
        {application.statusFlag && <StatusFlagBadge flag={application.statusFlag} />}
        {application.assignedTo && (
          <span style={{ fontSize: '13px', color: 'var(--color-dark-grey)' }}>
            Assigned to:{' '}
            <strong>
              {/* Bug #168: prefer server-resolved display name, then
                  fall back to the in-memory assignee list, then the raw UUID. */}
              {application.assignedToDisplayName
                || assignees.find((a) => a.id === application.assignedTo)?.displayName
                || application.assignedTo}
            </strong>
          </span>
        )}
      </div>

      {/* Feature #224: prior applications banner */}
      {priorApps && priorApps.count > 0 && (
        <div style={{
          padding: '10px 24px',
          backgroundColor: '#eff6ff',
          borderBottom: '1px solid #bfdbfe',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          fontSize: '13px',
          color: '#1e40af',
        }}>
          <Info size={16} style={{ flexShrink: 0 }} />
          <span>
            <strong>{priorApps.applicantFirstName || 'This applicant'}</strong> has applied{' '}
            {priorApps.count} time{priorApps.count !== 1 ? 's' : ''} before.{' '}
            <a
              href={`/tap/applications?search=${encodeURIComponent(priorApps.applicantEmail || '')}`}
              onClick={(e) => {
                e.preventDefault()
                navigate(`/tap/applications?search=${encodeURIComponent(priorApps.applicantEmail || '')}`)
              }}
              style={{ color: '#1e40af', fontWeight: 600, textDecoration: 'underline' }}
            >
              View those Applications
            </a>
          </span>
        </div>
      )}

      {/* Tab bar */}
      <div style={{
        display: 'flex',
        gap: '0',
        borderBottom: '2px solid var(--color-light-grey)',
        backgroundColor: 'var(--color-white)',
        flexWrap: 'wrap',
      }}>
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            style={{
              padding: '12px 20px',
              border: 'none',
              borderBottom: activeTab === tab.key ? '2px solid var(--color-magenta)' : '2px solid transparent',
              backgroundColor: 'transparent',
              color: activeTab === tab.key ? 'var(--color-magenta)' : 'var(--color-dark-grey)',
              fontWeight: activeTab === tab.key ? 600 : 400,
              fontSize: '14px',
              fontFamily: 'var(--font-family)',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              marginBottom: '-2px',
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div style={{ padding: '24px' }}>
        {renderTabContent()}
      </div>

      {/* Transition modal */}
      <Modal
        isOpen={transitionModalOpen}
        title="Transition Stage"
        onClose={() => setTransitionModalOpen(false)}
        isDirty={transitionStage !== '' || transitionNote !== ''}
        footer={
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
            <button onClick={() => setTransitionModalOpen(false)} style={secondaryButtonStyle}>Cancel</button>
            <button
              onClick={handleTransition}
              style={buttonStyle}
              // Feature #223: when transitioning to Outcome, also require a
              // chosen outcome before allowing submission.
              disabled={!transitionStage || (transitionStage === 'Outcome' && !decisionOutcome)}
            >
              Transition
            </button>
          </div>
        }
      >
        <div style={{ display: 'grid', gap: '16px' }}>
          <div>
            <div style={{ fontSize: '13px', color: 'var(--color-dark-grey)', marginBottom: '12px' }}>
              Current stage: <StageBadge stage={application.stage} />
            </div>
          </div>
          <div>
            <label style={labelStyle}>Move to stage</label>
            <select
              value={transitionStage}
              onChange={(e) => setTransitionStage(e.target.value)}
              style={{
                ...inputStyle,
                backgroundColor: 'var(--color-white)',
                cursor: 'pointer',
              }}
            >
              <option value="">Select stage...</option>
              {ALL_STAGES.filter((s) => s !== application.stage).map((s) => (
                <option key={s} value={s}>{stageLabels[s]}</option>
              ))}
            </select>
          </div>

          {/* Feature #223: when transitioning to Outcome, the user picks the
              decision inline. The three options here are the canonical
              outcomes specified on the work item. */}
          {transitionStage === 'Outcome' && (
            <div>
              <label style={labelStyle}>Outcome</label>
              <div style={{ fontSize: '12px', color: 'var(--color-dark-grey)', marginBottom: '8px' }}>
                Recording an outcome is a terminal action — for a <strong>Successful</strong> outcome, the onboarding handoff is triggered downstream.
              </div>
              <div style={{ display: 'grid', gap: '6px' }}>
                {([
                  { value: 'Successful', label: 'Successful', blurb: 'Offer accepted — proceed to onboarding.' },
                  { value: 'Unsuccessful', label: 'Unsuccessful', blurb: 'Candidate was not progressed.' },
                  { value: 'TalentPool', label: 'Talent Pool', blurb: 'Candidate was not progressed, added to Talent Pool.' },
                ] as { value: TapDecisionOutcome; label: string; blurb: string }[]).map((o) => (
                  <label
                    key={o.value}
                    style={{
                      display: 'flex',
                      gap: '10px',
                      padding: '10px 12px',
                      border: `1px solid ${decisionOutcome === o.value ? 'var(--color-magenta)' : 'var(--color-light-grey)'}`,
                      backgroundColor: decisionOutcome === o.value ? '#fdf2f8' : 'var(--color-white)',
                      borderRadius: '8px',
                      cursor: 'pointer',
                    }}
                  >
                    <input
                      type="radio"
                      name="transitionDecisionOutcome"
                      value={o.value}
                      checked={decisionOutcome === o.value}
                      onChange={() => setDecisionOutcome(o.value)}
                      style={{ marginTop: '3px' }}
                    />
                    <div>
                      <div style={{ fontSize: '14px', fontWeight: 600 }}>{o.label}</div>
                      <div style={{ fontSize: '12px', color: 'var(--color-dark-grey)' }}>{o.blurb}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          )}

          <div>
            <label style={labelStyle}>
              {transitionStage === 'Outcome' ? 'Decision note (optional)' : 'Note (optional)'}
            </label>
            <textarea
              value={transitionStage === 'Outcome' ? decisionNote : transitionNote}
              onChange={(e) => {
                if (transitionStage === 'Outcome') setDecisionNote(e.target.value)
                else setTransitionNote(e.target.value)
              }}
              rows={3}
              style={{ ...inputStyle, resize: 'vertical' }}
              placeholder={
                transitionStage === 'Outcome'
                  ? 'Any final context for the audit trail…'
                  : 'Add a note about this transition...'
              }
            />
          </div>
        </div>
      </Modal>

      {/* Flag modal */}
      <Modal
        isOpen={flagModalOpen}
        title="Set Status Flag"
        onClose={() => setFlagModalOpen(false)}
        size="sm"
        isDirty={selectedFlag !== (application?.statusFlag || '')}
        footer={
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
            <button onClick={() => setFlagModalOpen(false)} style={secondaryButtonStyle}>Cancel</button>
            <button onClick={handleFlag} style={buttonStyle}>Update Flag</button>
          </div>
        }
      >
        <div>
          <label style={labelStyle}>Status flag</label>
          <select
            value={selectedFlag}
            onChange={(e) => setSelectedFlag(e.target.value)}
            style={{
              ...inputStyle,
              backgroundColor: 'var(--color-white)',
              cursor: 'pointer',
            }}
          >
            <option value="">None (clear flag)</option>
            {ALL_FLAGS.filter(Boolean).map((f) => (
              <option key={f} value={f}>{FLAG_LABELS[f as string] ?? f}</option>
            ))}
          </select>
        </div>
      </Modal>

      {/* Assign modal (Bug #147 — picker replaces freeform text input) */}
      <Modal
        isOpen={assignModalOpen}
        title="Assign Application"
        onClose={() => setAssignModalOpen(false)}
        size="sm"
        isDirty={assignTo !== (application?.assignedTo || '')}
        footer={
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
            <button onClick={() => setAssignModalOpen(false)} style={secondaryButtonStyle}>Cancel</button>
            <button
              onClick={handleAssign}
              style={buttonStyle}
              // Bug #151: only allow submitting when there is something to do —
              // either a user is selected, or the record has an existing
              // assignee that the user is clearing. "Unassign with nobody
              // assigned" is a no-op and confused reviewers.
              disabled={
                assigneesLoading
                || (!assignTo && !application.assignedTo)
              }
            >
              {/*
                Bug #151: the label should reflect the *action*:
                  - selected someone       → Assign
                  - cleared selection and
                    someone is currently
                    assigned              → Unassign
                  - nothing to do           → Assign (disabled)
              */}
              {assignTo
                ? 'Assign'
                : application.assignedTo
                  ? 'Unassign'
                  : 'Assign'}
            </button>
          </div>
        }
      >
        <div>
          <label style={labelStyle}>Assign to</label>
          {assigneesLoading ? (
            <div style={{ fontSize: '13px', color: 'var(--color-dark-grey)', padding: '8px 0' }}>
              Loading eligible users…
            </div>
          ) : assigneesError ? (
            <div style={{ fontSize: '13px', color: 'var(--color-magenta)', padding: '8px 0' }}>
              {assigneesError}
            </div>
          ) : (
            <select
              value={assignTo}
              onChange={(e) => setAssignTo(e.target.value)}
              style={inputStyle}
            >
              <option value="">— Unassigned —</option>
              {assignees.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.displayName || u.email}
                  {u.jobTitle ? ` (${u.jobTitle})` : ''}
                  {u.email && u.displayName ? ` — ${u.email}` : ''}
                </option>
              ))}
            </select>
          )}
          {!assigneesLoading && !assigneesError && assignees.length === 0 && (
            <div style={{ fontSize: '12px', color: 'var(--color-dark-grey)', marginTop: '6px' }}>
              No users hold the manage_tap or platform_admin role yet. Grant one via Access Groups.
            </div>
          )}
        </div>
      </Modal>

      {/* Feature #223: standalone "Record Decision" modal removed. Decision
          capture is now inlined on the Transition modal when the user moves
          the application to Outcome. */}

      {/* Bug #240: Share Application modal. Top half lists existing active
          access grants with a revoke button; bottom half is a form for
          adding a new grant (user picker + Viewer/Approver radio +
          optional reason). The grantee receives an in-app notification
          with a deep link, so they can review even without a TAP role. */}
      <Modal
        isOpen={shareModalOpen}
        title="Share Application"
        size="lg"
        onClose={() => setShareModalOpen(false)}
        isDirty={Boolean(shareUserId || shareReason)}
        footer={
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
            <button onClick={() => setShareModalOpen(false)} style={secondaryButtonStyle}>Close</button>
          </div>
        }
      >
        <div style={{ display: 'grid', gap: '20px' }}>
          {/* Existing shares list */}
          <div>
            <div style={{ ...labelStyle, marginBottom: '8px' }}>Currently shared with</div>
            {accessGrantsLoading && (
              <div style={{ fontSize: '13px', color: 'var(--color-dark-grey)' }}>Loading…</div>
            )}
            {!accessGrantsLoading && accessGrants.length === 0 && (
              <div style={{ fontSize: '13px', color: 'var(--color-dark-grey)' }}>
                No active shares. Use the form below to grant access.
              </div>
            )}
            {!accessGrantsLoading && accessGrants.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {accessGrants.map((grant) => {
                  const u = shareableUsers.find((x) => x.id === grant.userId)
                  const display = u?.displayName || u?.email || grant.userId
                  return (
                    <div key={grant.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', border: '1px solid var(--color-light-grey)', borderRadius: '6px', fontSize: '13px' }}>
                      <div>
                        <div style={{ fontWeight: 500 }}>{display}</div>
                        <div style={{ fontSize: '12px', color: 'var(--color-dark-grey)' }}>
                          {grant.grantType} · granted {formatDateTime(grant.grantedAt)}
                          {grant.reason ? ` · ${grant.reason}` : ''}
                        </div>
                      </div>
                      <button
                        onClick={() => handleRevokeShare(grant)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', color: 'var(--color-magenta)' }}
                        title="Revoke access"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  )
                })}
              </div>
            )}
          </div>

          {/* Add new share */}
          <div style={{ borderTop: '1px solid var(--color-light-grey)', paddingTop: '16px' }}>
            <div style={{ ...labelStyle, marginBottom: '8px' }}>Add a new share</div>
            <div style={{ display: 'grid', gap: '12px' }}>
              {/* User picker — same combobox pattern as #230's Manager picker */}
              <div ref={shareUserDropdownRef} style={{ position: 'relative' }}>
                <label style={labelStyle}>Person</label>
                <input
                  value={shareUserSearch}
                  onChange={(e) => {
                    setShareUserSearch(e.target.value)
                    if (shareUserId) setShareUserId('')
                    setShareUserDropdownOpen(true)
                  }}
                  onFocus={() => setShareUserDropdownOpen(true)}
                  placeholder="Search by name or email…"
                  style={inputStyle}
                />
                {shareUserDropdownOpen && (
                  <div style={{
                    position: 'absolute', top: '100%', left: 0, right: 0,
                    maxHeight: '200px', overflowY: 'auto',
                    backgroundColor: 'var(--color-white)',
                    border: '1px solid var(--color-light-grey)',
                    borderRadius: '6px', marginTop: '4px', zIndex: 10,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  }}>
                    {(() => {
                      const q = shareUserSearch.toLowerCase()
                      const grantedIds = new Set(accessGrants.map((g) => g.userId))
                      const filtered = shareableUsers
                        .filter((u) => u.id !== application.assignedTo)
                        .filter((u) => !grantedIds.has(u.id))
                        .filter((u) => !q
                          || (u.displayName || '').toLowerCase().includes(q)
                          || (u.email || '').toLowerCase().includes(q))
                        .slice(0, 20)
                      if (filtered.length === 0) {
                        return (
                          <div style={{ padding: '8px 12px', fontSize: '13px', color: 'var(--color-dark-grey)' }}>
                            No matching users
                          </div>
                        )
                      }
                      return filtered.map((u) => (
                        <div
                          key={u.id}
                          onClick={() => {
                            setShareUserId(u.id)
                            setShareUserSearch(u.displayName || u.email)
                            setShareUserDropdownOpen(false)
                          }}
                          style={{ padding: '8px 12px', fontSize: '13px', cursor: 'pointer', borderBottom: '1px solid var(--color-light-grey)' }}
                          onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.backgroundColor = 'var(--color-light-grey)' }}
                          onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.backgroundColor = 'var(--color-white)' }}
                        >
                          <div style={{ fontWeight: 500 }}>{u.displayName || u.email}</div>
                          <div style={{ fontSize: '11px', color: 'var(--color-dark-grey)' }}>{u.email}</div>
                        </div>
                      ))
                    })()}
                  </div>
                )}
              </div>

              {/* Grant type radio */}
              <div>
                <label style={labelStyle}>Access level</label>
                <div style={{ display: 'flex', gap: '12px' }}>
                  {([
                    { value: 'Viewer' as const, label: 'Viewer', blurb: 'Can read the application' },
                    { value: 'Approver' as const, label: 'Approver', blurb: 'Can read + record an approval decision' },
                  ]).map((opt) => (
                    <label
                      key={opt.value}
                      style={{
                        flex: 1, display: 'flex', gap: '8px', padding: '10px 12px',
                        border: `1px solid ${shareGrantType === opt.value ? 'var(--color-magenta)' : 'var(--color-light-grey)'}`,
                        backgroundColor: shareGrantType === opt.value ? '#fdf2f8' : 'var(--color-white)',
                        borderRadius: '6px', cursor: 'pointer',
                      }}
                    >
                      <input
                        type="radio"
                        name="shareGrantType"
                        value={opt.value}
                        checked={shareGrantType === opt.value}
                        onChange={() => setShareGrantType(opt.value)}
                      />
                      <div>
                        <div style={{ fontSize: '13px', fontWeight: 600 }}>{opt.label}</div>
                        <div style={{ fontSize: '12px', color: 'var(--color-dark-grey)' }}>{opt.blurb}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label style={labelStyle}>Reason (optional)</label>
                <input
                  type="text"
                  value={shareReason}
                  onChange={(e) => setShareReason(e.target.value)}
                  placeholder="Shown to the recipient in the notification"
                  style={inputStyle}
                />
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  onClick={handleAddShare}
                  style={buttonStyle}
                  disabled={shareSaving || !shareUserId}
                >
                  {shareSaving ? 'Sharing…' : 'Share'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </Modal>

      {/* Feature #201 + #245: Typeform submission viewer.
          Originally a full-screen Modal, but Feature #245 changed it to a
          non-modal pinned side panel so the HR Agent can read the
          applicant's submission while editing the application
          (recording notes, updating Position Title, changing Stage,
          etc.) — no more closing the modal to make a change and
          reopening it to recover the context. The panel:

          * is fixed to the right edge of the viewport
          * has no backdrop, so clicks/keystrokes pass through to the
            page below
          * does not trap focus
          * does not auto-close on Escape (the explicit Close button is
            the only dismissal path — Escape would compete with the
            page's own keyboard handlers)

          When closed, nothing renders. */}
      {submissionModalOpen && (
        <div
          role="complementary"
          aria-label="Typeform Submission"
          style={{
            position: 'fixed',
            top: '80px',
            right: '16px',
            bottom: '16px',
            width: 'min(480px, calc(100vw - 32px))',
            backgroundColor: 'var(--color-white)',
            border: '1px solid var(--color-light-grey)',
            borderRadius: '8px',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
            display: 'flex',
            flexDirection: 'column',
            zIndex: 100,
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '12px 16px',
              borderBottom: '1px solid var(--color-light-grey)',
              flexShrink: 0,
            }}
          >
            <h3 style={{ margin: 0, fontSize: '15px', fontWeight: 600, color: 'var(--color-black)' }}>
              Typeform Submission
            </h3>
            <button
              onClick={() => setSubmissionModalOpen(false)}
              aria-label="Close submission viewer"
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: 'var(--color-dark-grey)',
                fontSize: '20px',
                lineHeight: 1,
                padding: '4px 8px',
              }}
            >
              ×
            </button>
          </div>
          <div style={{ flex: 1, overflowY: 'auto', padding: '16px' }}>
            {submissionLoading && (
              <div style={{ textAlign: 'center', padding: '40px', color: 'var(--color-dark-grey)' }}>
                Loading submission...
              </div>
            )}
            {!submissionLoading && !submission && (
              <div style={{ textAlign: 'center', padding: '40px', color: 'var(--color-dark-grey)' }}>
                No Typeform submission data found for this application.
              </div>
            )}
            {!submissionLoading && submission && (
              <>
                {submission.submittedAt && (
                  <div style={{ marginBottom: '16px', fontSize: '13px', color: 'var(--color-dark-grey)' }}>
                    Submitted: {formatDateTime(submission.submittedAt)}
                  </div>
                )}
                <div style={{ display: 'grid', gap: '16px' }}>
                  {submission.items.map((item, idx) => (
                    <div
                      key={idx}
                      style={{
                        borderBottom: idx < submission.items.length - 1 ? '1px solid var(--color-light-grey)' : 'none',
                        paddingBottom: '12px',
                      }}
                    >
                      <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--color-dark-grey)', marginBottom: '4px' }}>
                        {item.question}
                      </div>
                      <div style={{
                        fontSize: '14px',
                        color: item.answer ? 'var(--color-black)' : 'var(--color-dark-grey)',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                      }}>
                        {item.fieldType === 'file_upload' && item.answer ? (
                          <a href={item.answer} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--color-magenta)' }}>
                            {item.answer.split('/').pop() || 'Download file'}
                          </a>
                        ) : (
                          item.answer || '(no answer)'
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default ApplicationDetail
